import { RewriteFrames } from '@sentry/integrations';
import { Integrations } from '@sentry/tracing';
import getConfig from 'next/config';
import * as Sentry from '@sentry/node';
import { getCasID } from './cookies';
import readableStreamToJSON from './stream';
import { ABORT_REASON } from './customFetch';

// Custom Sentry tag names
export const SENTRY_CAS_TAG = 'cas';
export const SENTRY_ORIGIN_TAG = 'origin';
export const SENTRY_APOLLO_QUERY_NAME_TAG = 'query';

// Sentry origins - where errors come from
export const SENTRY_ORIGINS = {
  ERROR_PAGE: 'error-page',
  CROSSINGS_PAGE: 'crossings-page',
  DOCUMENT: '_document',
  OTHER: 'other',
  APOLLO: 'apollo',
};

// Function which creates our standard Sentry scope.
// Currently just adds the 'cas' and 'origin' tags
// to all Sentry logs. These can be overridden in
// individual captureException calls.
export const customSentryScope = (scope) => {
  if (typeof window === 'undefined') return scope;
  const casID = getCasID();
  scope.setTag(SENTRY_CAS_TAG, casID);
  scope.setTag(SENTRY_ORIGIN_TAG, SENTRY_ORIGINS.OTHER);
  return scope;
};

export const initSentry = () => {
  const { publicRuntimeConfig } = getConfig();

  const project = publicRuntimeConfig.SENTRY_NEXT_JS_PROJECT;
  const dsn = publicRuntimeConfig.SENTRY_PUBLIC_NEXT_JS_DSN;
  const release = publicRuntimeConfig.ENVIRONMENT.ReleaseVersion;
  const nextRoot = publicRuntimeConfig.NEXT_PUBLIC_SENTRY_SERVER_ROOT_DIR;
  const environment = publicRuntimeConfig.ENVIRONMENT.Name;
  const isValidDsn = dsn ? dsn.indexOf('sentry.io') > -1 : false;
  const isValidRelease = (release !== '#{Octopus.Release.Number}' || release !== 'Local' || !release);
  const isSentryEnabled =
    isValidDsn
    && isValidRelease
    && process.env.NODE_ENV === 'production';

  if (process.env.NEXT_IS_SERVER === 'true') {
    // eslint-disable-next-line
    console.log(`
Sentry Next JS Status ${isSentryEnabled ? '✔︝' : '❌ (running locally this is fine)'}
---------------------------------------------------------------------------------
  dsn: ${isValidDsn ? dsn : `invalid dsn: ${dsn}`}
  enabled: ${isSentryEnabled}
  production: ${process.env.NODE_ENV === 'production'}
  environment: ${environment}
  project: ${project}
  release: ${release}
---------------------------------------------------------------------------------
    `);
  }

  if (isSentryEnabled) { // double check to make sure we have a valid dsn injected
    const integrations = [];

    if (process.env.NEXT_IS_SERVER === 'true' && nextRoot) {
      // For Node.js, rewrite Error.stack to use relative paths, so that source
      // maps starting with ~/_next map to files in Error.stack with path
      // app:///_next
      integrations.push(
        new RewriteFrames({
          iteratee: (frame) => {
            // eslint-disable-next-line
            frame.filename = frame.filename.replace(nextRoot, 'app:///');
            // eslint-disable-next-line
            frame.filename = frame.filename.replace('.next', '_next');
            return frame;
          },
        }),
      );
    } else {
      integrations.push(new Integrations.BrowserTracing());
    }

    Sentry.init({
      integrations,
      dsn,
      release,
      environment,
      tracesSampleRate: publicRuntimeConfig.SENTRY_SAMPLE_RATE ?? 1.0,
      initialScope: customSentryScope,
      /**
       * We stop Sentry catching certain errors to stop it eating
       * up our quota with certain errors which are either not worth
       * capturing or shouldn't be captured at all.
       * The errors lister here are a genuine errors but they are a valid part
       * of the IBE's functionality, and they do not provide any meaningful
       * use when logged in Sentry.
       * All unfiltered errors are captured in CAS
       */
      ignoreErrors: [
        ABORT_REASON,
        // General Safari request failure, provides no additional information.
        /load failed/ig,
        // General non-Safari request failure, provides no additional information
        /failed to fetch/ig,
        // Failures in parsing a response body
        /ReadableStream/ig,
        // Errors when using an API not supported by a browser. E.g. permissions API,
        // location API, etc. These errors are non-fatal and users can continue using
        // the IBE when they occur
        /NotSupportedError/ig,
        // Usually CORS errors which are prominent mostly due to CAMC's third-party
        // dependencies.
        /SecurityError/ig,
      ],
      denyUrls: [
        // Any errors stemming from the Google API. Usually we cannot do anything about these.
        // We still capture errors from our code which uses the Maps API
        /https?:\/\/maps\.googleapis\.com\/[^\s"']*/ig,
      ],
    });
  }
};

/**
 * Captures an exception in Sentry with a CAS ID
 * @param {Error} exception
 * @param {string} origin
 * @param {Object} tags
 * @param {Object} extra
 * @returns {void}
 */
const captureException = (exception, origin, tags, extra) => {
  const casID = getCasID();
  Sentry.captureException(exception, {
    tags: {
      [SENTRY_CAS_TAG]: casID,
      [SENTRY_ORIGIN_TAG]: origin,
      ...tags,
    },
    extra,
  });
};

export const captureError = (error, origin = SENTRY_ORIGINS.OTHER, extraTags) => {
  // // Tag so we can search by it in Sentry
  const { info } = { ...error };
  // We need to remove the info property as it breaks Sentry reporting
  // This is just used to pass extra info into Sentry through a throw
  // Can't be copied to a new object as Errors can't be cloned
  // eslint-disable-next-line no-param-reassign
  delete error.info;
  captureException(error, origin, extraTags, info);
};

export const captureEvent = (event, origin = SENTRY_ORIGINS.OTHER) => {
  const casID = getCasID();
  // Tag so we can search by it in Sentry
  Sentry.captureEvent(event, {
    tags: {
      [SENTRY_CAS_TAG]: casID,
      [SENTRY_ORIGIN_TAG]: origin,
    },
  });
};

/**
 * Build an error object to send to Sentry using the 'captureError' helper
 * @param {string} message The actual error message
 * @param {Object} info An object with additional data to be logged to Sentry
 * @returns {Error} A JS Error class with an added 'info' field
 */
export const buildError = (message, info) => {
  const error = new Error(message);
  error.info = info;
  return error;
};

/**
 * Captures Apollo ErrorResponse errors and formats extra data
 * @param {import('apollo-link-error').ErrorResponse} errorEvent
 * @returns {void}
 */
export const captureApolloError = (errorEvent) => {
  if (!errorEvent) return;
  const { networkError, operation = {} } = errorEvent;
  const query = operation.operationName;
  const tags = {
    [SENTRY_APOLLO_QUERY_NAME_TAG]: query,
  };

  // If we have a networkError response, we can get lots more information
  // on the failure, including the response body which should include an
  // actual API error
  if (networkError.response) {
    const { body } = networkError.response;
    try {
      readableStreamToJSON(body).then((parsedBody) => {
        const errorContext = {
          // The response body we receive from the BE
          responseBody: parsedBody,
          // The request status code
          statusCode: networkError.statusCode,
          // The URL the request was made to, can help spot any query param issues
          url: networkError.response?.url,
          // The variables Apollo saw on the request, in the body or query
          variables: errorEvent.operation?.variables,
        };
        captureException(networkError, SENTRY_ORIGINS.APOLLO, tags, errorContext);
      });
    } catch (error) {
      captureException(error, SENTRY_ORIGINS.OTHER);
    }
    // Return early so we don't track the same error twice
    return;
  }

  captureException(
    networkError,
    SENTRY_ORIGINS.APOLLO,
    tags,
    operation,
  );
};

export default null;
